/**
 * Runtime Configuration Utility
 * 
 * This utility provides a way to access environment variables that can be:
 * 1. Set at runtime via window.ENV_CONFIG (for production Docker containers)
 * 2. Set at build time via process.env (for local development)
 * 
 * Priority: window.ENV_CONFIG > process.env > default value
 */

// Extend Window interface to include ENV_CONFIG
declare global {
  interface Window {
    ENV_CONFIG?: {
      REACT_APP_AZURE_CLIENT_ID?: string;
      REACT_APP_AZURE_REDIRECT_URI?: string;
      REACT_APP_AZURE_AUTHORITY_URL?: string;
      REACT_APP_SCOPE?: string;
      REACT_APP_API_URL?: string;
      REACT_APP_API_STRIPE_AUTH_URL?: string;
      REACT_APP_API_TOKEN?: string;
      REACT_APP_API_PREPROD_ACCESS_ID?: string;
      REACT_APP_SUPPORTED_COUNTRIES?: string;
      REACT_APP_PAYMENT_METHOD_CONFIG?: string;
      REACT_APP_EXTERNAL_USER_GROUP?: string;
    };
  }
}

/**
 * Get configuration value with runtime override support
 * @param key - The configuration key
 * @param defaultValue - Default value if not found
 * @returns The configuration value
 */
const getConfig = (key: string, defaultValue: string = ''): string => {
  // First try runtime config (for production)
  if (typeof window !== 'undefined' && window.ENV_CONFIG) {
    const runtimeValue = window.ENV_CONFIG[key as keyof typeof window.ENV_CONFIG];
    
    // Check if the value is a placeholder (hasn't been replaced by entrypoint)
    // Placeholders look like: __REACT_APP_AZURE_CLIENT_ID__
    if (runtimeValue && !runtimeValue.startsWith('__') && !runtimeValue.endsWith('__')) {
      return runtimeValue;
    }
  }
  
  // Fall back to build-time environment variable (for local development)
  const buildTimeValue = process.env[key];
  if (buildTimeValue) {
    return buildTimeValue;
  }
  
  // Return default value
  return defaultValue;
};

// Export typed configuration object
export const config = {
  azure: {
    clientId: getConfig('REACT_APP_AZURE_CLIENT_ID'),
    redirectUri: getConfig('REACT_APP_AZURE_REDIRECT_URI'),
    authorityUrl: getConfig('REACT_APP_AZURE_AUTHORITY_URL'),
    scope: getConfig('REACT_APP_SCOPE'),
  },
  api: {
    url: getConfig('REACT_APP_API_URL'),
    authUrl: getConfig('REACT_APP_API_STRIPE_AUTH_URL'),
    apiToken: getConfig('REACT_APP_API_TOKEN'),
    preprodAccessId: getConfig('REACT_APP_API_PREPROD_ACCESS_ID'),
  },
  supportedCountries: getConfig('REACT_APP_SUPPORTED_COUNTRIES', 'US,CA,AU,ES,GB,XI,IT,DE,FR,NL,IE,BE,LU'),
  paymentMethodConfig: getConfig('REACT_APP_PAYMENT_METHOD_CONFIG', ''),
  externalUserGroup: getConfig('REACT_APP_EXTERNAL_USER_GROUP', ''),
};

// Export the getConfig function for custom usage
export { getConfig };

